import { useEffect } from 'react';
import {
    Button,
    Input,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    Label,
} from '@ui';
import { useForm } from 'react-hook-form';
import { Package, CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { toastMessage } from '@ui/components/Toast';
import { cn } from '@ui/lib/utils';
import { useCreatePricingPlan, useUpdatePricingPlan } from '../hooks';
import type { PricingPlan, PricingPlanType, PricingPlanFormData } from '../types';

interface PricingPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    groupId: number;
    plan?: PricingPlan | null; // 수정 시 기존 데이터
    onSuccess?: () => void;
}

const TYPE_OPTIONS: { type: PricingPlanType; icon: typeof Package; label: string; description: string }[] = [
    { type: 'PACKAGE', icon: Package, label: '횟수제', description: '총 금액에 일정 횟수 제공' },
    { type: 'PREPAID', icon: CreditCard, label: '선불', description: '충전 금액 차감 방식' },
];

export function PricingPlanModal({ isOpen, onClose, groupId, plan, onSuccess }: PricingPlanModalProps) {
    const isEditMode = !!plan;

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        clearErrors,
        formState: { errors },
    } = useForm<PricingPlanFormData>({
        defaultValues: {
            name: '',
            type: 'PACKAGE',
            description: '',
            totalAmount: '',
            totalCount: '',
            chargeAmount: '',
        },
    });

    const selectedType = watch('type');
    const totalAmount = watch('totalAmount');
    const totalCount = watch('totalCount');

    // 회당 금액 계산 (횟수제)
    const perCountAmount = totalAmount && totalCount
        ? Math.round(Number(totalAmount.replace(/,/g, '')) / Number(totalCount))
        : 0;

    // 생성 mutation
    const { mutate: createPlan, isPending: isCreating } = useCreatePricingPlan({
        options: {
            onSuccess: () => {
                toastMessage({ message: '이용권이 등록되었습니다.', type: 'success' });
                handleClose();
                onSuccess?.();
            },
            onError: (error) => {
                const axiosError = error as { response?: { data?: { message?: string } } };
                toastMessage({ message: axiosError.response?.data?.message || '이용권 등록에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 수정 mutation
    const { mutate: updatePlan, isPending: isUpdating } = useUpdatePricingPlan({
        options: {
            onSuccess: () => {
                toastMessage({ message: '이용권이 수정되었습니다.', type: 'success' });
                handleClose();
                onSuccess?.();
            },
            onError: (error: any) => {
                toastMessage({ message: error?.response?.data?.message || '이용권 수정에 실패했습니다.', type: 'error' });
            },
        },
    });

    const isPending = isCreating || isUpdating;

    // 수정 모드일 때 기존 데이터로 폼 초기화
    useEffect(() => {
        if (isOpen && plan) {
            reset({
                name: plan.name,
                type: plan.type,
                description: plan.description || '',
                totalAmount: plan.totalAmount?.toLocaleString() || '',
                totalCount: plan.totalCount?.toString() || '',
                chargeAmount: plan.chargeAmount?.toLocaleString() || '',
            });
        } else if (isOpen && !plan) {
            reset({
                name: '',
                type: 'PACKAGE',
                description: '',
                totalAmount: '',
                totalCount: '',
                chargeAmount: '',
            });
        }
    }, [isOpen, plan, reset]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = (data: PricingPlanFormData) => {
        const parseNumber = (value: string) => {
            const parsed = Number(value.replace(/,/g, ''));
            return isNaN(parsed) ? undefined : parsed;
        };

        if (isEditMode && plan) {
            // 수정 (타입은 변경 불가)
            updatePlan({
                groupId,
                planId: plan.id,
                data: {
                    name: data.name,
                    description: data.description || undefined,
                    // 기존 타입에 맞는 값만 전송
                    totalAmount: plan.type === 'PACKAGE' ? parseNumber(data.totalAmount) : undefined,
                    totalCount: plan.type === 'PACKAGE' ? parseNumber(data.totalCount) : undefined,
                    chargeAmount: plan.type === 'PREPAID' ? parseNumber(data.chargeAmount) : undefined,
                },
            });
        } else {
            // 생성
            createPlan({
                groupId,
                name: data.name,
                type: data.type,
                description: data.description || undefined,
                ...(data.type === 'PACKAGE' && {
                    totalAmount: parseNumber(data.totalAmount),
                    totalCount: parseNumber(data.totalCount),
                }),
                ...(data.type === 'PREPAID' && {
                    chargeAmount: parseNumber(data.chargeAmount),
                }),
            });
        }
    };

    const handleTypeSelect = (type: PricingPlanType) => {
        setValue('type', type);
        // 타입 변경 시 관련 필드 에러 초기화
        clearErrors(['totalAmount', 'totalCount', 'chargeAmount']);
    };

    // 금액 입력 시 콤마 포맷팅
    const handleAmountChange = (field: 'totalAmount' | 'chargeAmount') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        const formatted = value ? Number(value).toLocaleString() : '';
        setValue(field, formatted);
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isEditMode ? '이용권 수정' : '이용권 추가'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* 이용권 타입 선택 */}
                    <div className="space-y-2">
                        <Label>이용권 타입 <span className="text-danger-500">*</span></Label>
                        {isEditMode ? (
                            /* 수정 모드: 현재 타입만 비활성화 상태로 표시 */
                            <div className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50">
                                {TYPE_OPTIONS.filter(opt => opt.type === selectedType).map(({ type, icon: Icon, label, description }) => (
                                    <div key={type} className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-neutral-200 dark:bg-neutral-700">
                                            <Icon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-neutral-700 dark:text-neutral-200">{label}</p>
                                            <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
                                        </div>
                                    </div>
                                ))}
                                <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 bg-neutral-200 dark:bg-neutral-700 px-2 py-1 rounded">
                                    변경 불가
                                </span>
                            </div>
                        ) : (
                            /* 생성 모드: 타입 선택 가능 */
                            <div className="grid grid-cols-3 gap-2">
                                {TYPE_OPTIONS.map(({ type, icon: Icon, label }) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => handleTypeSelect(type)}
                                        className={cn(
                                            'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all',
                                            selectedType === type
                                                ? 'border-orange-500 bg-orange-50 text-orange-600'
                                                : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
                                        )}
                                    >
                                        <Icon className="h-5 w-5" />
                                        <span className="text-sm font-medium">{label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t pt-4" />

                    {/* 이용권명 */}
                    <div className="space-y-2">
                        <Label htmlFor="plan-name" required>이용권명</Label>
                        <Input
                            id="plan-name"
                            placeholder="예: 중소형견 시간당"
                            error={!!errors.name}
                            {...register('name', {
                                required: '이용권명을 입력해주세요.',
                                maxLength: { value: 50, message: '최대 50자까지 입력 가능합니다.' },
                            })}
                        />
                        {errors.name && (
                            <p className="text-xs text-danger-500">{errors.name.message}</p>
                        )}
                    </div>

                    {/* 타입별 필드 - 횟수제 */}
                    {selectedType === 'PACKAGE' && (
                        <>
                            <div className="space-y-2">
                                <Label htmlFor="total-amount" required>총 금액</Label>
                                <div className="relative">
                                    <Input
                                        id="total-amount"
                                        placeholder="1,000,000"
                                        className="pr-8"
                                        {...register('totalAmount', {
                                            required: selectedType === 'PACKAGE' ? '총 금액을 입력해주세요.' : false,
                                            validate: (value) => {
                                                if (selectedType !== 'PACKAGE') return true;
                                                const num = Number(value.replace(/,/g, ''));
                                                if (isNaN(num) || num <= 0) return '올바른 금액을 입력해주세요.';
                                                return true;
                                            },
                                        })}
                                        onChange={handleAmountChange('totalAmount')}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 text-sm">원</span>
                                </div>
                                {errors.totalAmount && (
                                    <p className="text-xs text-danger-500">{errors.totalAmount.message}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="total-count" required>총 횟수</Label>
                                <div className="relative">
                                    <Input
                                        id="total-count"
                                        type="number"
                                        placeholder="10"
                                        className="pr-8"
                                        min={1}
                                        {...register('totalCount', {
                                            required: selectedType === 'PACKAGE' ? '총 횟수를 입력해주세요.' : false,
                                            min: { value: 1, message: '1회 이상이어야 합니다.' },
                                        })}
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 text-sm">회</span>
                                </div>
                                {errors.totalCount && (
                                    <p className="text-xs text-danger-500">{errors.totalCount.message}</p>
                                )}
                                {perCountAmount > 0 && (
                                    <p className="text-xs text-blue-600">
                                        회당 {perCountAmount.toLocaleString()}원
                                    </p>
                                )}
                            </div>
                        </>
                    )}

                    {/* 타입별 필드 - 선불 */}
                    {selectedType === 'PREPAID' && (
                        <div className="space-y-2">
                            <Label htmlFor="charge-amount" required>충전 금액</Label>
                            <div className="relative">
                                <Input
                                    id="charge-amount"
                                    placeholder="100,000"
                                    className="pr-8"
                                    {...register('chargeAmount', {
                                        required: selectedType === 'PREPAID' ? '충전 금액을 입력해주세요.' : false,
                                        validate: (value) => {
                                            if (selectedType !== 'PREPAID') return true;
                                            const num = Number(value.replace(/,/g, ''));
                                            if (isNaN(num) || num <= 0) return '올바른 금액을 입력해주세요.';
                                            return true;
                                        },
                                    })}
                                    onChange={handleAmountChange('chargeAmount')}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 text-sm">원</span>
                            </div>
                            {errors.chargeAmount && (
                                <p className="text-xs text-danger-500">{errors.chargeAmount.message}</p>
                            )}
                        </div>
                    )}

                    {/* 설명 */}
                    <div className="space-y-2">
                        <Label htmlFor="plan-description">설명</Label>
                        <textarea
                            id="plan-description"
                            className="flex min-h-[80px] w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="이용권에 대한 설명을 입력하세요"
                            maxLength={200}
                            {...register('description', {
                                maxLength: { value: 200, message: '최대 200자까지 입력 가능합니다.' },
                            })}
                        />
                        {errors.description && (
                            <p className="text-xs text-danger-500">{errors.description.message}</p>
                        )}
                    </div>

                    {/* 수정 시 안내 메시지 */}
                    {isEditMode && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-700">
                                수정 시 기존 구독 보호자에게는 영향이 없습니다.
                                신규 할당 시에만 변경된 내용이 반영됩니다.
                            </p>
                        </div>
                    )}

                    <DialogFooter className="gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            취소
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {isEditMode ? '저장 중...' : '등록 중...'}
                                </>
                            ) : (
                                isEditMode ? '변경 저장' : '이용권 등록'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
